import { keepPreviousData, useQuery } from '@tanstack/react-query';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { type LendingLoanListLoansData, type Loan, lendingLoanListLoansOptions } from '@column/api';
import { Button, Dropdown, Icon, formatNumber } from '@column/column-ui-kit';
import {
  Badge,
  DataTable,
  type DataTableColumnDef,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Timestamp,
} from '@column/ui';

import { PageActions } from '~/app/Layout/PageFrameContext';
import { PopoverFilterCreatedDate } from '~/app/pages/BankAccounts/_components/PopoverFilterCreatedDate';
import { FilterDropdown } from '~/app/pages/Transfers/Overview';
import { ROUTE } from '~/app/routes';
import { Loading, Pagination, PopoverFilterTransferDateData, PopoverFilterWrapper } from '~/components';
import { Search } from '~/components/Search';
import { LogoLoading } from '~/elements';
import { useDebounce } from '~/hooks/useDebounce';
import { useQueryParams } from '~/hooks/useQueryParams';
import { usePaginationState } from '~/lib/api/hooks/usePaginationState';
import { FeatureFlag, useFeatureFlag } from '~/lib/flags';
import { useNavigate } from '~/lib/navigation';
import { TableTimeFormat } from '~/repositories/UserRepository';
import { PAGE_SIZE_OPTIONS, useTableConfig } from '~/stores/Config';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Flex, Inner } from '~/styles';
import { formatDate, formatDateTime, formatString, getFormattedDate } from '~/util';

const MobileSafariButton = styled(Button)`
  /* Mobile Safari specific fixes */
  -webkit-appearance: none !important;
  appearance: none !important;
  -webkit-tap-highlight-color: transparent !important;
  background: transparent !important;
  background-color: transparent !important;

  /* Force override any inherited background */
  &::before,
  &::after {
    -webkit-appearance: none !important;
    appearance: none !important;
  }

  /* Additional mobile Safari fixes */
  @media screen and (-webkit-min-device-pixel-ratio: 0) {
    -webkit-appearance: none !important;
    appearance: none !important;
    background: transparent !important;
    background-color: transparent !important;
  }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
  padding: 0 var(--page-margin) 12px;
`;

const ToolbarGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const ResetFilter = styled(Button)`
  padding-left: 0;
  padding-right: 0;
`;

const LoanSearch = styled(Search)`
  max-width: 100%;
  transition: width 0.2s;

  label {
    flex-grow: 1;
    min-width: 0;
  }

  input {
    min-width: 0;
  }
`;

const LoanFilterDropdown = styled(FilterDropdown)`
  min-width: 0;
`;

const LoanStatusBadge = styled(Badge)<{ $type: 'default' | 'info' }>`
  && {
    background-color: ${({ $type, theme }) =>
      $type === 'info' ? theme.style.chipInfoBackgroundColor : theme.style.chipDefaultBackgroundColor};
    color: ${({ $type, theme }) =>
      $type === 'info' ? theme.style.chipInfoTextColor : theme.style.chipDefaultTextColor};
    box-shadow: ${({ $type, theme }) =>
      `${theme.style.chipDefaultBorderProperties} ${theme.style.chipDefaultBorderWidth} ${
        $type === 'info' ? theme.style.chipInfoBorderColor : theme.style.chipDefaultBorderColor
      }`};
    border-radius: ${({ theme }) => theme.style.chipDefaultBorderRadius};
    font-size: ${({ theme }) => theme.style.chipDefaultFontSize};
    font-weight: ${({ theme }) => theme.style.chipDefaultFontWeight};
  }
`;

const STATUS_OPTIONS = [
  { value: 'current', label: 'Current' },
  { value: 'delinquent', label: 'Delinquent' },
  { value: 'charged_off', label: 'Charged off' },
  { value: 'in_dispute', label: 'In Dispute' },
  { value: 'canceled', label: 'Canceled' },
  { value: 'paid_off', label: 'Paid off' },
];

type LoanStatusFilter = '' | (typeof STATUS_OPTIONS)[number]['value'];

interface LoanFilters {
  status: LoanStatusFilter;
  query: string;
  date: Partial<PopoverFilterTransferDateData>;
}

interface LoanFilterUrlParams {
  status?: string;
  query?: string;
  'created.gte'?: string;
  'created.lt'?: string;
  starting_after?: string;
  page_index?: string;
}

const EMPTY_FILTERS: LoanFilters = {
  status: '',
  query: '',
  date: {},
};

interface OptionalDateRangeInput {
  startDateString?: string;
  endDateString?: string;
}

const startOfDay = (dateString: string): string | undefined => {
  const date = new Date(`${dateString}T00:00:00`);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString();
};

const startOfNextDay = (dateString: string): string | undefined => {
  const date = new Date(`${dateString}T00:00:00`);
  if (Number.isNaN(date.getTime())) return undefined;
  date.setDate(date.getDate() + 1);
  return date.toISOString();
};

const convertOptionalDatesToRange = ({
  startDateString,
  endDateString,
}: OptionalDateRangeInput): Partial<PopoverFilterTransferDateData> => {
  const createdAtRange: Partial<PopoverFilterTransferDateData> = {};

  if (startDateString) {
    createdAtRange.createdAtGte = startOfDay(startDateString);
  }

  if (endDateString) {
    createdAtRange.createdAtLt = startOfNextDay(endDateString);
  }

  return createdAtRange;
};

const isLoanStatusFilter = (value: string): value is LoanStatusFilter =>
  value === '' || STATUS_OPTIONS.some((option) => option.value === value);

const filtersFromUrl = (params: LoanFilterUrlParams | undefined, isLoanSearchEnabled: boolean): LoanFilters => {
  if (!params) return EMPTY_FILTERS;

  return {
    status: params.status && isLoanStatusFilter(params.status) ? params.status : '',
    query: isLoanSearchEnabled ? (params.query ?? '') : '',
    date: {
      createdAtGte: params['created.gte'],
      createdAtLt: params['created.lt'],
    },
  };
};

const filtersToUrl = (filters: LoanFilters, isLoanSearchEnabled: boolean): LoanFilterUrlParams => {
  const params: LoanFilterUrlParams = {};
  if (filters.status) params.status = filters.status;
  if (isLoanSearchEnabled && filters.query) params.query = filters.query;
  if (filters.date.createdAtGte) params['created.gte'] = filters.date.createdAtGte;
  if (filters.date.createdAtLt) params['created.lt'] = filters.date.createdAtLt;
  return params;
};

const buildQuery = (
  filters: LoanFilters,
  pageSize: number,
  cursor: string | undefined,
  isLoanSearchEnabled: boolean
) => {
  const query: NonNullable<LendingLoanListLoansData['query']> = {
    limit: pageSize,
  };

  if (cursor) query.starting_after = cursor;
  if (filters.status) query.status = filters.status;
  if (filters.date.createdAtGte) query['created.gte'] = filters.date.createdAtGte;
  if (filters.date.createdAtLt) query['created.lt'] = filters.date.createdAtLt;
  if (isLoanSearchEnabled && filters.query) query.query = filters.query;

  return query;
};

const renderDate = (value: string | Date | null | undefined) => {
  if (!value) return <span>-</span>;

  return <span>{formatDate(value, { dateFormat: 'short', timeZoneFormat: 'local' })}</span>;
};

export const PageLoans: React.FC = () => {
  const navigate = useNavigate();
  const isLoanSearchEnabled = useFeatureFlag(FeatureFlag.EnableLoanSearch);
  const { currentPlatform } = useSessionStore();
  const { pageSize, timeFormat, update: updateTableConfig } = useTableConfig('loans');
  const { addDangerNotification } = useNotificationStore();
  const { queryParams, setQueryParams } = useQueryParams<LoanFilterUrlParams>();
  const initialPagination = useRef({
    cursor: queryParams?.starting_after ? String(queryParams.starting_after) : undefined,
    pageIndex: Number(queryParams?.page_index) || 0,
  });
  const {
    cursor,
    onNext,
    onPrevious,
    reset: resetPagination,
    setPageSize,
    pageIndex,
    hasPrevious,
  } = usePaginationState({
    defaultPageSize: pageSize,
    initialCursor: initialPagination.current.cursor,
    initialPageIndex: initialPagination.current.pageIndex,
  });

  const [filters, setFilters] = useState<LoanFilters>(() => filtersFromUrl(queryParams, isLoanSearchEnabled));
  const [queryInput, setQueryInput] = useState(() => filtersFromUrl(queryParams, isLoanSearchEnabled).query);
  const [entriesOpen, setEntriesOpen] = useState(false);

  useEffect(() => {
    if (isLoanSearchEnabled) return;
    setFilters((prev) => (prev.query ? { ...prev, query: '' } : prev));
    setQueryInput('');
  }, [isLoanSearchEnabled]);

  useEffect(() => {
    const base = filtersToUrl(filters, isLoanSearchEnabled);
    setQueryParams(() => ({
      ...base,
      ...(cursor ? { starting_after: cursor } : {}),
      ...(pageIndex > 0 ? { page_index: String(pageIndex) } : {}),
    }));
  }, [filters, cursor, pageIndex, isLoanSearchEnabled, setQueryParams]);

  const updateFilter = <K extends keyof LoanFilters>(key: K, value: LoanFilters[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const [debouncedApplyQuery, cancelApplyQuery] = useDebounce((value: string) => {
    setFilters((prev) => ({ ...prev, query: value }));
  }, 300);

  const handleQueryInputChange = (value: string) => {
    setQueryInput(value);
    debouncedApplyQuery(value);
  };

  const handleQuerySubmit = (value: string) => {
    cancelApplyQuery();

    if (showEnterHint) {
      navigate(`${ROUTE.LOANS}/edit/${loans[0].id}`);
      return;
    }
    setQueryInput(value);
    setFilters((prev) => ({ ...prev, query: value }));
  };

  const handleFilterReset = () => {
    cancelApplyQuery();
    setFilters(EMPTY_FILTERS);
    setQueryInput('');
  };

  const isDateFilterActive = Boolean(filters.date.createdAtGte || filters.date.createdAtLt);
  const isFilterActive = Boolean(filters.status || isDateFilterActive || (isLoanSearchEnabled && filters.query));

  const query = useMemo(
    () => buildQuery(filters, pageSize, cursor, isLoanSearchEnabled),
    [filters, pageSize, cursor, isLoanSearchEnabled]
  );

  const isInitialFilterRenderRef = useRef(true);
  useEffect(() => {
    if (isInitialFilterRenderRef.current) {
      isInitialFilterRenderRef.current = false;
      return;
    }
    resetPagination();
  }, [filters, resetPagination]);

  const {
    data: response,
    isLoading,
    isFetching,
  } = useQuery({
    ...lendingLoanListLoansOptions({ query }),
    enabled: Boolean(currentPlatform?.id),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });

  const loans = response?.loans ?? [];
  const hasMore = response?.has_more ?? false;
  const showEnterHint =
    Boolean(filters.query) && !isFetching && filters.query === queryInput && loans.length === 1 && !hasMore;
  const activePageSize = Number(pageSize);
  const tableMinHeight = loans.length === 0 ? 341 : undefined;

  const handlePageSizeChange = (value: number) => {
    setPageSize(value);
    updateTableConfig({ limit: value }).catch(() => {
      addDangerNotification({ content: 'Failed to save page size preference' });
    });
  };

  const handleTimeOptionChange = (value: TableTimeFormat | undefined) => {
    if (!value) return;
    updateTableConfig({ timeFormat: value }).catch(() => {
      addDangerNotification({ content: 'Failed to save time format preference' });
    });
  };

  const handlePageNext = () => {
    if (loans.length > 0) {
      onNext({ cursor: loans[loans.length - 1].id });
    }
  };

  const columns: DataTableColumnDef<Loan>[] = [
    {
      accessor: 'description',
      header: 'Description',
      width: 'minmax(300px, 2fr)',
      className: 'min-w-0',
      cell: ({ row }) => <span className="min-w-0 truncate font-medium text-foreground">{row.description ?? ''}</span>,
    },
    {
      accessor: 'status',
      header: 'Status',
      width: 'minmax(min-content, 120px)',
      cell: ({ row }) => (
        <LoanStatusBadge variant="ghost" $type={row.status === 'current' ? 'info' : 'default'}>
          {formatString(row.status)}
        </LoanStatusBadge>
      ),
    },
    {
      accessor: 'maturity_date',
      header: 'Maturity Date',
      width: 'minmax(140px, max-content)',
      cell: ({ row }) => renderDate(row.maturity_date),
    },
    {
      accessor: 'balances',
      header: 'Outstanding Balance',
      width: 'min-content',
      cell: ({ row }) => <span>{formatNumber(parseInt(row.balances?.principal_outstanding ?? '0', 10))}</span>,
    },
    {
      accessor: 'created_at',
      header: (
        <Dropdown
          size="small"
          positionRight
          active={timeFormat}
          options={[
            { label: 'Relative Time', value: TableTimeFormat.Relative },
            { label: 'Date / Time', value: TableTimeFormat.Absolute },
          ]}
          onChange={handleTimeOptionChange}
          customLabel={
            <Flex alignItems="center" gap={4}>
              Created <Icon.ChevronDown />
            </Flex>
          }
        />
      ),
      width: 'min-content',
      cell: ({ row }) => {
        if (!row.created_at) return null;

        return (
          <Timestamp
            date={row.created_at}
            align="right"
            className="text-xs text-muted-foreground"
            formatHover={(hoverDate) =>
              formatDateTime(hoverDate, {
                timeZoneFormat: timeFormat === TableTimeFormat.Relative ? 'local' : 'utc',
              })
            }
          >
            {timeFormat === TableTimeFormat.Relative
              ? getFormattedDate(row.created_at, 'time-passed')
              : formatDateTime(row.created_at, { timeZoneFormat: 'local' })}
          </Timestamp>
        );
      },
    },
  ];

  return (
    <>
      <PageActions>
        <Button
          onClick={() => navigate(`${ROUTE.LOANS}/disbursements`)}
          icon={<Icon.MoneyOut />}
          size="small"
          variant="secondary"
        >
          Disbursements
        </Button>

        <Button
          onClick={() => navigate(`${ROUTE.LOANS}/payments`)}
          icon={<Icon.MoneyIn />}
          size="small"
          variant="secondary"
        >
          Payments
        </Button>

        <MobileSafariButton onClick={() => navigate(`${ROUTE.LOANS}/create`)} icon={<Icon.Plus />}>
          Create Loan
        </MobileSafariButton>
      </PageActions>

      <Toolbar>
        <ToolbarGroup>
          <LoanFilterDropdown
            size="small"
            active={filters.status}
            $isFilterActive={Boolean(filters.status)}
            options={STATUS_OPTIONS}
            label={STATUS_OPTIONS.find((option) => option.value === filters.status)?.label ?? 'Status'}
            onChange={(value: LoanStatusFilter) => updateFilter('status', value)}
          />
          <PopoverFilterWrapper
            label="Created"
            isActive={isDateFilterActive}
            onSubmit={(data: Partial<PopoverFilterTransferDateData>) => updateFilter('date', data)}
          >
            <PopoverFilterCreatedDate data={filters.date} convertDateRange={convertOptionalDatesToRange} />
          </PopoverFilterWrapper>
          {isLoanSearchEnabled && (
            <LoanSearch
              value={queryInput}
              placeholder="Search loans..."
              onValueChange={handleQueryInputChange}
              onFilterSubmit={handleQuerySubmit}
              showEnterHint={showEnterHint}
              width={250}
            />
          )}
          {isFilterActive && (
            <ResetFilter size="small" variant="subtle-primary" icon={<Icon.Reset />} onClick={handleFilterReset}>
              Reset
            </ResetFilter>
          )}
        </ToolbarGroup>
        <ToolbarGroup />
      </Toolbar>

      <Loading isLoading={isLoading}>
        <Inner pt={0} px="var(--page-margin)">
          <DataTable<Loan>
            columns={columns}
            data={loans}
            rowKey={(row) => row.id}
            isLoading={isFetching}
            loadingContent={<LogoLoading />}
            style={{ minHeight: tableMinHeight }}
            emptyContent={
              <Empty className="min-h-[300px]">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <Icon.HandCoins />
                  </EmptyMedia>
                  <EmptyTitle>No loans found</EmptyTitle>
                  <EmptyDescription>
                    {isFilterActive
                      ? 'No loans were found matching the specified filters'
                      : 'Create your first loan to get started.'}
                  </EmptyDescription>
                </EmptyHeader>
                {!isFilterActive && (
                  <EmptyContent className="pointer-events-auto">
                    <Button onClick={() => navigate(`${ROUTE.LOANS}/edit`)} size="small" icon={<Icon.Plus />}>
                      Create Loan
                    </Button>
                  </EmptyContent>
                )}
              </Empty>
            }
            rowClassName="min-h-11"
            onRowClick={(row) => {
              navigate(`${ROUTE.LOANS}/edit/${row.id}`);
            }}
          />

          <div className="relative z-[1] flex items-center justify-between pt-4">
            <Dropdown
              options={PAGE_SIZE_OPTIONS.map((entries: number) => ({
                label: entries.toString(),
                value: entries,
              }))}
              size="small"
              variant="muted"
              active={activePageSize}
              customLabel={
                <Badge
                  variant="secondary"
                  className="rounded-[8px] [&>svg]:ml-[-4px] [&>svg]:inline-block [&>svg]:align-top"
                >
                  {activePageSize}
                  {entriesOpen ? <Icon.ChevronUp /> : <Icon.ChevronDown />}
                </Badge>
              }
              onOpenChange={setEntriesOpen}
              onChange={handlePageSizeChange}
            />
            <Pagination
              current={pageIndex + 1}
              disablePrev={!hasPrevious}
              disableNext={!hasMore}
              onPrev={onPrevious}
              onNext={handlePageNext}
            />
          </div>
        </Inner>
      </Loading>
    </>
  );
};
